import React from 'react';
import {Box, Divider, Typography} from '@mui/material';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

let gisLoadPromise: Promise<void> | null = null;

const loadGoogleIdentityServices = (): Promise<void> => {
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }
  if (!gisLoadPromise) {
    gisLoadPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('Failed to load Google Sign-In')));
        return;
      }
      const script = document.createElement('script');
      script.src = GIS_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google Sign-In'));
      document.head.appendChild(script);
    });
  }
  return gisLoadPromise;
};

type GoogleSignInButtonProps = {
  onCredential: (idToken: string) => void;
  onError?: (message: string) => void;
};

const GoogleSignInButton = ({onCredential, onError}: GoogleSignInButtonProps) => {
  const buttonRef = React.useRef<HTMLDivElement>(null);
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const onCredentialRef = React.useRef(onCredential);
  onCredentialRef.current = onCredential;

  React.useEffect(() => {
    if (!clientId || !buttonRef.current) {
      return;
    }

    let cancelled = false;

    loadGoogleIdentityServices()
      .then(() => {
        if (cancelled || !buttonRef.current || !window.google?.accounts?.id) {
          return;
        }
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => {
            if (response.credential) {
              onCredentialRef.current(response.credential);
            } else {
              onError?.('Auth failed.');
            }
          }
        });
        buttonRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(buttonRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          width: 280
        });
      })
      .catch(() => {
        if (!cancelled) {
          onError?.('Google Sign-In is unavailable.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, onError]);

  if (!clientId) {
    return null;
  }

  return (
    <Box sx={{width: '100%', mt: 1, mb: 2}}>
      <Divider sx={{my: 2}}>
        <Typography variant="body2" color="text.secondary">
          or
        </Typography>
      </Divider>
      <Box ref={buttonRef} sx={{display: 'flex', justifyContent: 'center'}} />
    </Box>
  );
};

export default GoogleSignInButton;
